/**
 * Timer Management Utility
 *
 * Centralized timer management for game state transitions.
 * Keeps timers outside of Zustand store to maintain state serializability for Ably sync.
 *
 * This manager tracks all active setTimeout calls and provides cleanup functionality
 * to prevent memory leaks when components unmount or game state resets.
 */

class TimerManager {
  private activeTimers = new Set<NodeJS.Timeout>();

  /**
   * Create a managed setTimeout that will be tracked and can be cleaned up
   */
  createTimer(callback: () => void, delay: number): NodeJS.Timeout {
    const timerId = setTimeout(() => {
      callback();
      // Auto-remove from tracking after execution
      this.activeTimers.delete(timerId);
    }, delay);

    this.activeTimers.add(timerId);
    return timerId;
  }

  /**
   * Clear all active timers
   * Call this on component unmount or game reset
   */
  clearAll(): void {
    this.activeTimers.forEach((timerId) => {
      clearTimeout(timerId);
    });
    this.activeTimers.clear();
  }

  /**
   * Clear a specific timer
   */
  clearTimer(timerId: NodeJS.Timeout): void {
    clearTimeout(timerId);
    this.activeTimers.delete(timerId);
  }

  /**
   * Get count of active timers (for debugging)
   */
  getActiveCount(): number {
    return this.activeTimers.size;
  }
}

// Singleton instance
export const gameTimerManager = new TimerManager();
